/**
 * Comprehensive Motion Types System based on Robert's Rules of Order
 * Organized by class with proper precedence ordering
 */

import { RONR_RANK_BY_KEY, getTopPendingMotion } from './ronrRanks.js';

export const MOTION_CLASSES = {
  PRIVILEGED: 'Privileged',
  SUBSIDIARY: 'Subsidiary',
  MAIN: 'Main',
  INCIDENTAL: 'Incidental',
  /** RONR Table II "class B": motions that bring a question again before the assembly */
  BRING_BACK: 'Bring Back'
};

export const VOTE_REQUIREMENTS = {
  MAJORITY: 'majority',
  TWO_THIRDS: '2/3',
  UNANIMOUS: 'unanimous',
  CHAIR_DECIDES: 'chair_decides',
  /** RONR_taxonomy: no assembly vote (e.g. orders of the day, parliamentary inquiry) */
  NONE: 'none',
};

export const FLOW_TYPES = {
  STANDARD: 'STANDARD',
  CHAIR: 'CHAIR',
  INFO: 'INFO',
  DIRECT_VOTE: 'DIRECT_VOTE',
};

/**
 * Complete motion definitions with RONR properties
 * Precedence: Lower number = higher precedence within class
 */
export const MOTION_TYPES = {
  // PRIVILEGED MOTIONS (precedence 1-5, highest to lowest)
  'FIX_HOUR': {
    key: 'FIX_HOUR',
    name: 'Fix the Hour',
    class: MOTION_CLASSES.PRIVILEGED,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 1,
    needsSecond: true,
    debatable: false,
    amendable: true,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: true,
    canInterrupt: false,
    verbiageState: 'It is moved to fix the hour for [time].',
    description: 'Sets or changes the time for adjournment or recess'
  },

  'ADJ': {
    key: 'ADJ',
    name: 'Adjourn',
    class: MOTION_CLASSES.PRIVILEGED,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 2,
    needsSecond: true,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: false,
    canInterrupt: false,
    verbiageState: 'It is moved to adjourn.',
    description: 'Ends the meeting'
  },

  'RECESS': {
    key: 'RECESS',
    name: 'Recess',
    class: MOTION_CLASSES.PRIVILEGED,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 3,
    needsSecond: true,
    debatable: false,
    amendable: true,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: false,
    canInterrupt: false,
    verbiageState: 'It is moved to recess for [time].',
    description: 'Suspends the meeting for a specified time'
  },

  'QUESTION_PRIVILEGE': {
    key: 'QUESTION_PRIVILEGE',
    name: 'Question of Privilege',
    class: MOTION_CLASSES.PRIVILEGED,
    flowType: FLOW_TYPES.CHAIR,
    precedence: 4,
    needsSecond: false,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.CHAIR_DECIDES,
    canReconsider: false,
    canInterrupt: true,
    verbiageState: 'I rise to a question of privilege.',
    description: 'Addresses urgent matters affecting the assembly or individual members'
  },

  'CALL_ORDERS': {
    key: 'CALL_ORDERS',
    name: 'Call for the Orders of the Day',
    class: MOTION_CLASSES.PRIVILEGED,
    flowType: FLOW_TYPES.INFO,
    precedence: 5,
    needsSecond: false,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.NONE,
    canReconsider: false,
    canInterrupt: true,
    verbiageState: 'I call for the orders of the day.',
    description: 'Demands adherence to the adopted agenda or program'
  },

  // SUBSIDIARY MOTIONS (precedence 1-8, highest to lowest)
  'TABLE': {
    key: 'TABLE',
    name: 'Lay on the Table',
    class: MOTION_CLASSES.SUBSIDIARY,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 1,
    needsSecond: true,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: false,
    canInterrupt: false,
    verbiageState: 'It is moved to lay the pending question on the table.',
    description: 'Temporarily disposes of the pending question'
  },

  'PREVQ': {
    key: 'PREVQ',
    name: 'Previous Question',
    class: MOTION_CLASSES.SUBSIDIARY,
    flowType: FLOW_TYPES.DIRECT_VOTE,
    precedence: 2,
    needsSecond: true,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.TWO_THIRDS,
    canReconsider: true,
    canInterrupt: false,
    verbiageState: 'I move the previous question.',
    description: 'Immediately closes debate and forces a vote'
  },

  'LIMIT_DEBATE': {
    key: 'LIMIT_DEBATE',
    name: 'Limit or Extend Limits of Debate',
    class: MOTION_CLASSES.SUBSIDIARY,
    flowType: FLOW_TYPES.DIRECT_VOTE,
    precedence: 3,
    needsSecond: true,
    debatable: false,
    amendable: true,
    voteRequired: VOTE_REQUIREMENTS.TWO_THIRDS,
    canReconsider: true,
    canInterrupt: false,
    verbiageState: 'I move to limit debate to [time limit].',
    description: 'Sets or changes time limits on debate'
  },

  'POSTPONE': {
    key: 'POSTPONE',
    name: 'Postpone to a Certain Time',
    class: MOTION_CLASSES.SUBSIDIARY,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 4,
    needsSecond: true,
    debatable: true,
    amendable: true,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: true,
    canInterrupt: false,
    verbiageState: 'It is moved to postpone this matter to [time].',
    description: 'Defers action on a motion to a specified time',
    voteNote: 'A two-thirds vote is required to postpone the question as (or into) a special order.'
  },

  'COMMIT': {
    key: 'COMMIT',
    name: 'Commit or Refer',
    class: MOTION_CLASSES.SUBSIDIARY,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 5,
    needsSecond: true,
    debatable: true,
    amendable: true,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: true,
    canInterrupt: false,
    verbiageState: 'It is moved to refer this matter to [committee].',
    description: 'Sends a motion to a committee for detailed study'
  },

  'AMEND': {
    key: 'AMEND',
    name: 'Amend',
    class: MOTION_CLASSES.SUBSIDIARY,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 6,
    needsSecond: true,
    debatable: true,
    amendable: true,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: true,
    canInterrupt: false,
    verbiageState: 'It is moved to amend by [amendment].',
    description: 'Modifies the pending motion'
  },

  'POSTPONE_INDEF': {
    key: 'POSTPONE_INDEF',
    name: 'Postpone Indefinitely',
    class: MOTION_CLASSES.SUBSIDIARY,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 7,
    needsSecond: true,
    debatable: true,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: true,
    canInterrupt: false,
    verbiageState: 'It is moved to postpone this motion indefinitely.',
    description: 'Kills the main motion without a direct vote on it'
  },

  // MAIN MOTION
  'MAIN': {
    key: 'MAIN',
    name: 'Main Motion',
    class: MOTION_CLASSES.MAIN,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 1,
    needsSecond: true,
    debatable: true,
    amendable: true,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: true,
    canInterrupt: false,
    verbiageState: 'It is moved that [motion]. The question is on the adoption of the motion.',
    description: 'Brings new business before the assembly'
  },

  // INCIDENTAL MOTIONS (no precedence order - handled as they arise)
  'POINT_ORDER': {
    key: 'POINT_ORDER',
    name: 'Point of Order',
    class: MOTION_CLASSES.INCIDENTAL,
    flowType: FLOW_TYPES.CHAIR,
    precedence: null,
    needsSecond: false,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.CHAIR_DECIDES,
    canReconsider: false,
    canInterrupt: true,
    verbiageState: 'I rise to a point of order.',
    description: 'Calls attention to a violation of rules or proper procedure'
  },

  'PARL_INQUIRY': {
    key: 'PARL_INQUIRY',
    name: 'Parliamentary Inquiry',
    class: MOTION_CLASSES.INCIDENTAL,
    flowType: FLOW_TYPES.INFO,
    precedence: null,
    needsSecond: false,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.NONE,
    canReconsider: false,
    canInterrupt: true,
    verbiageState: 'I rise to a parliamentary inquiry.',
    description: 'Requests information on parliamentary procedure'
  },

  'APPEAL': {
    key: 'APPEAL',
    name: 'Appeal',
    class: MOTION_CLASSES.INCIDENTAL,
    flowType: FLOW_TYPES.CHAIR,
    precedence: null,
    needsSecond: true,
    debatable: true,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: false,
    canInterrupt: true,
    verbiageState: 'I appeal from the decision of the chair.',
    description: 'Asks the assembly to overturn the chair\'s ruling (RONR taxonomy)'
  },

  'POINT_INFO': {
    key: 'POINT_INFO',
    name: 'Point of Information',
    class: MOTION_CLASSES.INCIDENTAL,
    flowType: FLOW_TYPES.INFO,
    precedence: null,
    needsSecond: false,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.NONE,
    canReconsider: false,
    canInterrupt: true,
    verbiageState: 'I rise to a point of information.',
    description: 'Requests factual information relevant to the business'
  },

  'WITHDRAW_MOTION': {
    key: 'WITHDRAW_MOTION',
    name: 'Withdraw a Motion',
    class: MOTION_CLASSES.INCIDENTAL,
    precedence: null,
    needsSecond: false,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: false,
    canInterrupt: false,
    verbiageState: 'I ask unanimous consent to withdraw my motion.',
    description: 'Removes a motion from consideration'
  },

  'DIVISION_QUESTION': {
    key: 'DIVISION_QUESTION',
    name: 'Division of a Question',
    class: MOTION_CLASSES.INCIDENTAL,
    precedence: null,
    needsSecond: true,
    debatable: false,
    amendable: true,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: true,
    canInterrupt: false,
    verbiageState: 'I move to divide the question.',
    description: 'Separates a motion into parts for separate consideration'
  },

  'DIVISION_ASSEMBLY': {
    key: 'DIVISION_ASSEMBLY',
    name: 'Division of the Assembly',
    class: MOTION_CLASSES.INCIDENTAL,
    precedence: null,
    needsSecond: false,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.NONE,
    canReconsider: false,
    canInterrupt: true,
    verbiageState: 'I call for a division.',
    description: 'Demands a standing vote or more exact count'
  },

  'SUSPEND_RULES': {
    key: 'SUSPEND_RULES',
    name: 'Suspend the Rules',
    class: MOTION_CLASSES.INCIDENTAL,
    precedence: null,
    needsSecond: true,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.TWO_THIRDS,
    canReconsider: false,
    canInterrupt: false,
    verbiageState: 'I move to suspend the rules that interfere with [purpose].',
    description: 'Temporarily sets aside rules for a specific purpose',
    voteNote: 'A two-thirds vote suspends a rule of order; a standing rule of the assembly may be suspended by a majority vote.'
  },

  'OBJECT_CONSIDERATION': {
    key: 'OBJECT_CONSIDERATION',
    name: 'Object to Consideration of a Question',
    class: MOTION_CLASSES.INCIDENTAL,
    precedence: null,
    needsSecond: false,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.TWO_THIRDS,
    canReconsider: true,
    canInterrupt: true,
    verbiageState: 'I object to the consideration of this question.',
    description: 'Prevents discussion of an inappropriate or sensitive main motion'
  },

  // MOTIONS THAT BRING A QUESTION AGAIN BEFORE THE ASSEMBLY (RONR Table II, class B)
  // In order only when no question is pending (floor is clear).
  'RECONSIDER': {
    key: 'RECONSIDER',
    name: 'Reconsider',
    class: MOTION_CLASSES.BRING_BACK,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 1,
    needsSecond: true,
    // Debatable if the motion to be reconsidered is debatable; treated as debatable here.
    debatable: true,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: false,
    canInterrupt: false,
    verbiageState: 'I move to reconsider the vote on [motion].',
    description: 'Brings back a motion decided earlier in the same session for a fresh vote (mover must have voted with the prevailing side)'
  },

  'TAKE_FROM_TABLE': {
    key: 'TAKE_FROM_TABLE',
    name: 'Take from the Table',
    class: MOTION_CLASSES.BRING_BACK,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 2,
    needsSecond: true,
    debatable: false,
    amendable: false,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: false,
    canInterrupt: false,
    verbiageState: 'I move to take from the table the motion relating to [subject].',
    description: 'Resumes consideration of a motion that was laid on the table'
  },

  'RESCIND': {
    key: 'RESCIND',
    name: 'Rescind / Amend Something Previously Adopted',
    class: MOTION_CLASSES.BRING_BACK,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 3,
    needsSecond: true,
    debatable: true,
    amendable: true,
    // Two-thirds, OR a majority with previous notice, OR a majority of the entire membership.
    voteRequired: VOTE_REQUIREMENTS.TWO_THIRDS,
    canReconsider: false,
    canInterrupt: false,
    verbiageState: 'I move to rescind (or amend) the previously adopted motion relating to [subject].',
    description: 'Repeals or amends a motion adopted earlier',
    voteNote: 'Two-thirds vote, OR a majority with previous notice, OR a majority of the entire membership.'
  },

  'RATIFY': {
    key: 'RATIFY',
    name: 'Ratify',
    class: MOTION_CLASSES.BRING_BACK,
    flowType: FLOW_TYPES.STANDARD,
    precedence: 4,
    needsSecond: true,
    debatable: true,
    amendable: true,
    voteRequired: VOTE_REQUIREMENTS.MAJORITY,
    canReconsider: false,
    canInterrupt: false,
    verbiageState: 'I move to ratify the action taken relating to [subject].',
    description: 'Confirms and validates action already taken without proper authority'
  }
};

/**
 * RONR Table II reconsideration rules, kept as a single source of truth.
 * Values: 'yes' | 'no' | 'negative_only' | 'affirmative_only'.
 * canReconsider is derived so that older callers stay in sync.
 */
export const RECONSIDER_RULES = {
  MAIN: 'yes',
  POSTPONE_INDEF: 'affirmative_only',
  AMEND: 'yes',
  COMMIT: 'yes',
  POSTPONE: 'yes',
  LIMIT_DEBATE: 'yes',
  PREVQ: 'yes',
  TABLE: 'negative_only',
  FIX_HOUR: 'yes',
  ADJ: 'no',
  RECESS: 'no',
  QUESTION_PRIVILEGE: 'no',
  CALL_ORDERS: 'no',
  POINT_ORDER: 'no',
  PARL_INQUIRY: 'no',
  APPEAL: 'yes',
  POINT_INFO: 'no',
  WITHDRAW_MOTION: 'negative_only',
  DIVISION_QUESTION: 'no',
  DIVISION_ASSEMBLY: 'no',
  SUSPEND_RULES: 'no',
  OBJECT_CONSIDERATION: 'negative_only',
  RECONSIDER: 'no',
  TAKE_FROM_TABLE: 'no',
  RESCIND: 'yes',
  RATIFY: 'yes'
};

Object.entries(RECONSIDER_RULES).forEach(([key, rule]) => {
  if (MOTION_TYPES[key]) {
    MOTION_TYPES[key].reconsider = rule;
    MOTION_TYPES[key].canReconsider = rule !== 'no';
  }
});

/**
 * Get motions organized by class with proper precedence sorting
 */
export const getMotionsByClass = () => {
  const motionsByClass = {};

  // Group motions by class
  Object.values(MOTION_TYPES).forEach(motion => {
    if (!motionsByClass[motion.class]) {
      motionsByClass[motion.class] = [];
    }
    motionsByClass[motion.class].push(motion);
  });

  // Sort within each class by precedence (lower number = higher precedence)
  Object.keys(motionsByClass).forEach(className => {
    motionsByClass[className].sort((a, b) => {
      // Incidental motions have no precedence, sort alphabetically
      if (className === MOTION_CLASSES.INCIDENTAL) {
        return a.name.localeCompare(b.name);
      }
      // For other classes, sort by precedence
      return (a.precedence || 999) - (b.precedence || 999);
    });
  });

  return motionsByClass;
};

/**
 * Rough, hand-tuned estimate of how often each motion type comes up in an
 * ordinary meeting — used only to ORDER the "Smart" motion list, never to
 * gate availability (isMotionInOrder still governs what's actually usable).
 */
const MOTION_LIKELIHOOD_WEIGHT = {
  MAIN: 10,
  AMEND: 9,
  PREVQ: 8,
  COMMIT: 6,
  POSTPONE: 6,
  TABLE: 5,
  POINT_ORDER: 5,
  DIVISION_ASSEMBLY: 5,
  PARL_INQUIRY: 5,
  LIMIT_DEBATE: 4,
  RECESS: 4,
  ADJ: 4,
  POINT_INFO: 4,
  WITHDRAW_MOTION: 3,
  RECONSIDER: 3,
  POSTPONE_INDEF: 3,
  QUESTION_PRIVILEGE: 3,
  TAKE_FROM_TABLE: 3,
  APPEAL: 2,
  CALL_ORDERS: 2,
  DIVISION_QUESTION: 2,
  SUSPEND_RULES: 2,
  OBJECT_CONSIDERATION: 1,
  FIX_HOUR: 1,
  RESCIND: 1,
  RATIFY: 1,
};

/**
 * Order motions by what's most useful to see next: currently in-order motions
 * first, ranked by how often they typically come up, then by RONR precedence
 * as a tiebreaker. Not-in-order motions still appear further down (so the
 * chair can see what's coming) — use the separate "In order only" sort to
 * hide them entirely.
 */
export const getMotionsBySmartContext = (currentMotion, motionStack = []) => {
  const sorted = Object.values(MOTION_TYPES).sort((a, b) => {
    const aInOrder = isMotionInOrder(a.key, currentMotion, motionStack);
    const bInOrder = isMotionInOrder(b.key, currentMotion, motionStack);
    if (aInOrder !== bInOrder) return aInOrder ? -1 : 1;

    const aWeight = MOTION_LIKELIHOOD_WEIGHT[a.key] || 0;
    const bWeight = MOTION_LIKELIHOOD_WEIGHT[b.key] || 0;
    if (aWeight !== bWeight) return bWeight - aWeight;

    return (a.precedence || 999) - (b.precedence || 999);
  });

  return { 'Suggested order': sorted };
};

/**
 * Get motion precedence value for motion stack ordering
 * Lower number = higher precedence
 */
export const getMotionPrecedence = (motionKey) => {
  const motion = MOTION_TYPES[motionKey];
  if (!motion) return 999;

  // Single source of truth: the global RONR ranks (ronrRanks.js).
  // Lower return value = higher precedence (i.e. nearer the top of the stack).
  // Incidental motions arise out of the immediately pending question and are
  // decided before it, so they take the highest precedence.
  if (motion.class === MOTION_CLASSES.INCIDENTAL) return 0;

  const rank = RONR_RANK_BY_KEY[motionKey];
  if (rank === undefined) return 999; // bring-back / unranked motions sit at the bottom
  return 100 - rank;
};

/**
 * Check if a motion can interrupt another motion
 */
export const canMotionInterrupt = (motionKey) => {
  const motion = MOTION_TYPES[motionKey];
  return motion?.canInterrupt || false;
};

/**
 * RONR_taxonomy.md §3: ranked motions use global rank; incidentals use null (bypass rank inequality).
 */
const getRonrRankForOrderCheck = (motionKey) => {
  const meta = MOTION_TYPES[motionKey];
  if (!meta) return undefined;
  if (meta.class === MOTION_CLASSES.INCIDENTAL) return null;
  const r = RONR_RANK_BY_KEY[motionKey];
  return r === undefined ? undefined : r;
};

/**
 * Count how many pending motions (current + stacked) are of a given type.
 */
const countPendingByType = (type, currentMotion, motionStack) => {
  let count = 0;
  if (currentMotion?.type === type) count += 1;
  (motionStack || []).forEach((m) => {
    if (m?.type === type) count += 1;
  });
  return count;
};

/**
 * Check if a motion is in order given current parliamentary state
 */
export const isMotionInOrder = (motionKey, currentMotion, motionStack) => {
  const motion = MOTION_TYPES[motionKey];
  if (!motion) return false;

  switch (motion.class) {
    case MOTION_CLASSES.PRIVILEGED:
    case MOTION_CLASSES.SUBSIDIARY: {
      // RONR_taxonomy §3.2–3.3: empty floor only Main; else new rank must be > immediately pending rank.
      const top = getTopPendingMotion(currentMotion, motionStack);
      if (!top) return false;

      // Amend is a special case: RONR permits two degrees of amendment, and Amend
      // applies to the *immediately pending* question whenever that question is
      // amendable — not only to the main motion. (This is what enables a secondary
      // amendment: amending a pending primary amendment.)
      if (motionKey === 'AMEND') {
        const pendingAmendments = countPendingByType('AMEND', currentMotion, motionStack);
        if (pendingAmendments >= 2) return false; // no third-degree amendment
        return Boolean(MOTION_TYPES[top.type]?.amendable);
      }

      const newRank = getRonrRankForOrderCheck(motionKey);
      const topRank = getRonrRankForOrderCheck(top.type);
      if (newRank === undefined) return false;
      if (topRank === null) return true;
      if (!(newRank > topRank)) return false;

      if (motion.class === MOTION_CLASSES.SUBSIDIARY) {
        const hasMainMotion =
          currentMotion?.type === 'MAIN' ||
          (motionStack || []).some((m) => MOTION_TYPES[m.type]?.class === MOTION_CLASSES.MAIN);
        if (!hasMainMotion) return false;
      }
      return true;
    }

    case MOTION_CLASSES.MAIN:
    case MOTION_CLASSES.BRING_BACK:
      // RONR Hierarchy: Main = Rank 1 (lowest). Main and "bring back" motions are only
      // in order when no question is on the floor and nothing is held on the stack.
      if (currentMotion) return false;
      if (motionStack.length > 0) return false;
      return true;

    case MOTION_CLASSES.INCIDENTAL:
      // Incidental motions depend on specific circumstances
      return isIncidentalMotionInOrder(motionKey, currentMotion, motionStack);

    default:
      return false;
  }
};

/**
 * Specific logic for when incidental motions are in order
 */
const isIncidentalMotionInOrder = (motionKey, currentMotion, motionStack) => {
  switch (motionKey) {
    case 'POINT_ORDER':
    case 'PARL_INQUIRY':
    case 'POINT_INFO':
    case 'APPEAL':
      // These can interrupt anytime
      return true;

    case 'WITHDRAW_MOTION':
      // Can only withdraw your own motion and only before it's stated
      return currentMotion != null;

    case 'DIVISION_QUESTION':
      // Can only divide a motion that hasn't been voted on
      return currentMotion != null;

    case 'DIVISION_ASSEMBLY':
      // A single member may demand a division (a retake by rising vote) whenever a
      // question is before the assembly or a vote has just been taken by voice.
      // It requires no second and no vote, so it is in order whenever a question is present.
      return currentMotion != null;

    case 'OBJECT_CONSIDERATION':
      // Before debate has progressed (stated-only legacy, or immediately after state when debate is open)
      return currentMotion?.type === 'MAIN' &&
        (currentMotion.status === 'stated' || currentMotion.status === 'open_debate');

    default:
      return true;
  }
};

export default MOTION_TYPES;